import React from "react";
import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Download,
  MoreHorizontal,
  Camera,
  Edit,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  XCircle,
  AlertOctagon,
  Layers,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { cn, viIncludes } from "@/lib/utils";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { SortableHeader, type SortOrder } from "@/components/shared/SortableHeader";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { QCProcessModalContainer } from "./QCProcessModalContainer";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { QCStatus } from "@/config/status-registry";
import { ListPageShell } from "@/components/shared/ListPageShell";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "processId", label: "Mã Phiếu", width: 16 },
  { key: "productionOrderId", label: "Mã Lệnh SX", width: 16 },
  { key: "processStep", label: "Công đoạn", width: 18 },
  { key: "machineId", label: "Thiết bị", width: 16 },
  { key: "status", label: "Trạng thái", width: 16 },
  { key: "notes", label: "Ghi chú", width: 25 },
];
// ── Build sidebar items from registry ──
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

export default function QCProcessPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useRoleAccess();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(20);
  const { headerSearch } = useHeaderSearch();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const handleSort = (field: string, order: SortOrder) => {
    setSortField(order ? field : null);
    setSortOrder(order);
    setCurrentPage(1);
  };
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingCheck, setEditingCheck] = useState<any>(null);
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("all");

  // Fetch ALL data for sidebar counts
  const { data, isLoading, error } = useQuery({
    queryKey: ["qc-process-all", dateParams],
    // Quay lại danh sách sau khi xoá/sửa ở trang chi tiết luôn tải lại, không hiện bản cũ (FB-7KL5H3)
    refetchOnMount: "always",
    queryFn: async () => {
      const paginatedData = await trpcClient.qcProcess.list.query({
        page: 1,
        limit: 100,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      });
      return paginatedData.items || [];
    },
  });

  // Fetch stats for 4 KPI cards
  const { data: statsData } = useQuery({
    queryKey: ["qc-process-stats", dateParams],
    refetchOnMount: "always",
    queryFn: async () => (trpcClient as any).qcProcess.getStats.query(),
  });

  const allItems: any[] = data || [];

  // Client-side filter + paginate
  const filteredData = useMemo(() => {
    let result: any[] = allItems;
    if (statusFilter && statusFilter !== "ALL") {
      if (statusFilter === "FIRST_PIECE") {
        result = result.filter((item) => item.checkType === "FIRST_PIECE" || !item.checkType);
      } else if (statusFilter === "PASSED") {
        result = result.filter((item) => item.status === "PASSED" || item.status === "pass");
      } else if (statusFilter === "FAILED") {
        result = result.filter((item) => item.status === "FAILED" || item.status === "fail");
      } else {
        result = result.filter((item) => item.status === statusFilter);
      }
    }

    // Ô tìm kiếm trên header (?q): mã phiếu, lệnh sản xuất, công đoạn, thiết bị, người kiểm
    if (headerSearch.trim()) {
      result = result.filter((item) =>
        [
          item.processId,
          item.id,
          item.productionOrder?.orderId,
          item.productionOrderId,
          item.processStep,
          item.machine?.name,
          item.machineId,
          item.inspectorName,
          item.inspector?.fullName,
          item.notes,
        ].some((v) => viIncludes(v == null ? "" : String(v), headerSearch.trim())),
      );
    }

    // Sắp xếp theo cột
    if (sortField && sortOrder) {
      const accessors: Record<string, (i: any) => any> = {
        processId: (i) => i.processId || i.id,
        productionOrder: (i) => i.productionOrder?.orderId || i.productionOrderId,
        checkType: (i) => i.checkType || "FIRST_PIECE",
        checkTime: (i) => new Date(i.checkTime || i.checkDate || i.date || i.createdAt).getTime(),
      };
      const accessor = accessors[sortField];
      if (accessor) {
        const dir = sortOrder === "asc" ? 1 : -1;
        result = [...result].sort((a, b) => {
          const va = accessor(a);
          const vb = accessor(b);
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
          if (typeof va === "number" && typeof vb === "number") return ((Number.isNaN(va) ? 0 : va) - (Number.isNaN(vb) ? 0 : vb)) * dir;
          return String(va).localeCompare(String(vb), "vi") * dir;
        });
      }
    }
    return result;
  }, [allItems, statusFilter, headerSearch, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedItems = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const rowIds = paginatedItems.map((i: any) => i.id);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    clearSelection,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    selectedCount,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    filename: "qc_cong_doan",
    columns: EXPORT_COLUMNS,
    localData: filteredData,
    selectedIds: Array.from(selectedIds),
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    return [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
  };

  const handleSave = async (formData: any) => {
    try {
      if (editingCheck) {
        await trpcClient.qcProcess.update.mutate({
          id: editingCheck.id,
          data: formData,
        });
      } else {
        await trpcClient.qcProcess.create.mutate(formData);
      }
      setShowAddSheet(false);
      setEditingCheck(null);
      await queryClient.invalidateQueries({ queryKey: ["qc-process-all"] });
    } catch (error: any) {
      toast.error(error?.message || "Không thể lưu kết quả QC");
    }
  };

  const stats = statsData || {
    total: allItems.length,
    firstPiecePassed: allItems.filter(
      (x) =>
        (x.checkType === "FIRST_PIECE" || !x.checkType) &&
        (x.status === "PASSED" || x.status === "pass"),
    ).length,
    firstPieceTotal:
      allItems.filter((x) => x.checkType === "FIRST_PIECE").length ||
      allItems.length,
    passed: allItems.filter(
      (x) => x.status === "PASSED" || x.status === "pass",
    ).length,
    failed: allItems.filter(
      (x) => x.status === "FAILED" || x.status === "fail",
    ).length,
  };

  const handleQuickCreateNcr = (item: any) => {
    navigate({
      to: "/ncr/create",
      search: {
        sourceType: "IPQC",
        sourceId: item.id,
      } as any,
    });
  };

  const qcProcKpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: "ALL",
      label: "Tổng Kiểm Công Đoạn",
      count: stats.total,
      icon: Layers,
      variant: "default",
      tooltip: "Lượt kiểm tra phân xưởng",
    },
    {
      key: "FIRST_PIECE",
      label: "Nghiệm Thu Đầu Ca",
      count: `${stats.firstPiecePassed} / ${stats.firstPieceTotal || stats.firstPiecePassed}`,
      icon: CheckCircle2,
      variant: "emerald",
      tooltip: "Mẫu bài in/bế đạt mở máy",
    },
    {
      key: "PASSED",
      label: "Kiểm Tra Định Kỳ Ca",
      count: stats.passed,
      icon: Activity,
      variant: "blue",
      tooltip: "Vận hành máy ổn định",
    },
    {
      key: "FAILED",
      label: "Dừng Máy / Phế Phẩm",
      count: stats.failed,
      icon: XCircle,
      variant: "rose",
      animate: stats.failed > 0,
      tooltip: "Cần KCS xử lý lỗi/NCR",
    },
  ], [stats]);

  const activeKpiKey = statusFilter || "ALL";

  const handleSelectKpi = (key: string | null) => {
    if (!key || key === "ALL") {
      setStatusFilter(null);
    } else {
      setStatusFilter(statusFilter === key ? null : key);
    }
  };

  return (
    <>
      <ListPageShell
        trashModule="qc-process"
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              className={emsTabClass(true, { size: "sm" })}
            >
              <Activity className="w-3.5 h-3.5 text-indigo-600" />
              <span>Kiểm Tra IPQC</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
                {allItems.length}
              </span>
            </button>
          </div>
        }
        dateFilter={
          <DatePeriodFilter
            period={period}
            setPeriod={setPeriod}
            customFrom={customFrom}
            setCustomFrom={setCustomFrom}
            customTo={customTo}
            setCustomTo={setCustomTo}
          />
        }
        headerFilters={
          <CompactKpiRibbon
            items={qcProcKpiItems}
            activeKey={activeKpiKey}
            onSelect={handleSelectKpi}
            isLoading={isLoading}
            testIdPrefix="qc-process-kpi"
            className="w-auto"
          />
        }
        onExport={() => handleExport()}
        exportLoading={isExporting}
        unitLabel="phiếu"
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={filteredData.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        actions={
          hasPermission("qcProcess", "create") ? (
            <Button
              size="sm"
              className="h-7 px-2.5 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 shrink-0"
              onClick={() => navigate({ to: "/qc-process/create" })}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span>Lập Phiếu IPQC</span>
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full min-h-[400px] text-red-500">
            Lỗi khi tải dữ liệu. Vui lòng thử lại.
          </div>
        ) : (
          <Table className={cn("min-w-[1250px]", selectionMode && "apv-select-mode")}>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <TableHead
                    className={cn("apv-col-checkbox w-10 text-center px-4")}
                  >
                    <DataTableSelectAllCheckbox
                      rowSelection={
                        { allSelected, toggleSelectAll, selectionMode } as any
                      }
                      asChild
                    />
                  </TableHead>
                  <SortableHeader label="Mã phiếu" field="processId" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="apv-col-code font-semibold w-[160px]" />
                  <SortableHeader label="Lệnh SX & Công đoạn" field="productionOrder" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold min-w-[240px]" />
                  <SortableHeader label="Phân loại & Mẫu" field="checkType" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="font-semibold min-w-[160px]" />
                  <TableHead className="font-semibold min-w-[200px]">
                    Checklist & Ghi chú
                  </TableHead>
                  <SortableHeader label="Ngày & Người kiểm" field="checkTime" currentField={sortField} currentOrder={sortOrder} onSort={handleSort} className="text-center font-semibold w-[160px]" />
                  <TableHead className="apv-col-actions w-[60px] text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="h-32 text-center text-slate-400"
                    >
                      {statusFilter
                        ? "Không có phiếu nào ở trạng thái này"
                        : "Chưa có phiếu QC công đoạn nào"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((item: any) => (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      rowSelection={
                        {
                          selectedIds,
                          handleRowMouseDown,
                          handleRowMouseUp,
                          handleRowMouseLeave,
                          wasLongPress,
                          selectionMode,
                          handleRowSelect,
                        } as any
                      }
                      onClick={(e) => {
                        if (selectionMode) return;
                        navigate({
                          to: "/qc-process/$checkId",
                          params: { checkId: item.id },
                        });
                      }}
                    >
                      <TableCell
                        className={cn("px-4 apv-col-checkbox !bg-white dark:!bg-slate-900 isolation-isolate")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DataTableRowCheckbox
                          id={item.id}
                          rowSelection={{ selectedIds, toggleSelect } as any}
                          asChild
                        />
                      </TableCell>
                      <TableCell className="apv-col-code py-1.5 !bg-white dark:!bg-slate-900 isolation-isolate">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className="font-bold text-indigo-600 text-[13px] leading-none group-hover:underline">
                            {item.processId || item.id}
                          </span>
                          <StatusBadge type="QCStatus" value={item.status} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-900 text-[13px] leading-none">
                            {item.productionOrder?.orderId ||
                              item.productionOrderId}
                          </span>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0 border-slate-200 leading-tight"
                            >
                              {item.processStep}
                            </Badge>
                            <span className="text-[10px] text-slate-400 font-mono leading-none">
                              {item.machine?.name || item.machineId || "—"}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          {item.checkType === "FIRST_PIECE" || !item.checkType ? (
                            <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] font-bold px-1.5 py-0">
                              ⭐ Đầu ca (First-Piece)
                            </Badge>
                          ) : item.checkType === "ROUTINE" ? (
                            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-[10px] px-1.5 py-0">
                              ⏱️ Định kỳ ca
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">
                              🔍 Tuần tra xưởng
                            </Badge>
                          )}
                          <div className="flex items-center gap-2 text-[11px] font-mono mt-0.5">
                            <span className="text-slate-500">
                              Mẫu: <b className="text-slate-800">{item.qtySample || 5}</b>
                            </span>
                            <span
                              className={
                                (item.qtyDefect || 0) > 0
                                  ? "text-rose-600 font-bold"
                                  : "text-slate-400"
                              }
                            >
                              Lỗi: {item.qtyDefect || 0}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const list = Array.isArray(item.checklist)
                            ? item.checklist
                            : typeof item.checklist === "string"
                            ? (() => { try { return JSON.parse(item.checklist); } catch { return []; } })()
                            : item.checklist && typeof item.checklist === "object"
                            ? Object.values(item.checklist)
                            : [];
                          const okCount = Array.isArray(list) ? list.filter((c: any) => c?.result === "OK").length : 0;
                          const ngCount = Array.isArray(list) ? list.filter((c: any) => c?.result !== "OK").length : 0;
                          return (
                            <div className="flex flex-col max-w-[250px] gap-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[12px] font-medium text-slate-700 flex items-center gap-1 leading-none">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                  {okCount} OK
                                </span>
                                {ngCount > 0 && (
                                  <span className="text-[12px] font-medium text-rose-600 flex items-center gap-1 leading-none">
                                    <AlertTriangle className="w-3 h-3" />
                                    {ngCount} NG
                                  </span>
                                )}
                              </div>
                              {item.notes && (
                                <span
                                  className="text-[11px] text-slate-500 italic truncate leading-none"
                                  title={item.notes}
                                >
                                  "{item.notes}"
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[12px] text-slate-700 font-medium leading-none">
                            {typeof item.inspector === "object"
                              ? item.inspector?.fullName
                              : item.inspector}
                          </span>
                          <span className="text-[11px] text-slate-500 font-mono leading-none">
                            {new Date(item.checkTime).toLocaleDateString("vi-VN")}{" "}
                            {new Date(item.checkTime).toLocaleTimeString(
                              "vi-VN",
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className="apv-col-actions px-4 text-right !bg-white dark:!bg-slate-900 isolation-isolate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-slate-200"
                            >
                              <MoreHorizontal className="h-4 w-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-[170px]">
                            <DropdownMenuLabel>Thao tác</DropdownMenuLabel>
                            {hasPermission("qcProcess", "update") && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingCheck(item);
                                  setShowAddSheet(true);
                                }}
                              >
                                <Edit className="w-4 h-4 mr-2 text-slate-500" />{" "}
                                Chỉnh sửa
                              </DropdownMenuItem>
                            )}
                            {(item.status === "FAILED" || item.status === "fail") && (
                              <DropdownMenuItem
                                className="text-rose-600 focus:text-rose-700"
                                onClick={() => handleQuickCreateNcr(item)}
                              >
                                <AlertOctagon className="w-4 h-4 mr-2" /> Tạo NCR Sự Cố
                              </DropdownMenuItem>
                            )}
                            {/* Ảnh lỗi là tệp đính kèm của phiếu, xem ở trang chi tiết */}
                            <DropdownMenuItem
                              onClick={() => navigate({ to: "/qc-process/$checkId", params: { checkId: item.id } })}
                            >
                              <Camera className="w-4 h-4 mr-2 text-slate-500" />{" "}
                              Xem ảnh lỗi
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </DataTableRow>
                  ))
                )}
              </TableBody>
            </Table>
        )}
      </ListPageShell>

      <QCProcessModalContainer
        open={showAddSheet}
        onOpenChange={(open) => {
          setShowAddSheet(open);
          if (!open) setEditingCheck(null);
        }}
        onSubmit={handleSave}
        initialData={editingCheck}
      />

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
